import { apiClient } from '@/api/client';
import { config } from '@/api/config';
import { calculateReadingTime } from '@/features/feed/utils';
import type { Content, ContentTheme, FeedItem } from '@/types';

// Pagination constants - must match backend FeedConfig.defaultLimit
export const FEED_PAGE_SIZE = 5;
// Trigger next page fetch when this many items from the end
export const FEED_SCROLL_THRESHOLD = 3;

export interface GetFeedParams {
  limit?: number;
  offset?: number;
  language?: string;
}

export interface GetFeedResponse {
  items: FeedItem[];
  hasMore: boolean;
}

// Backend ContentTheme enum values
type HermesContentTheme =
  | 'AI'
  | 'Engineering'
  | 'Product'
  | 'Design'
  | 'Startups'
  | 'Business'
  | 'Tools';

// Tipo da resposta do Hermes (exported for testing)
export interface HermesScoredContent {
  content: {
    id: string;
    title: string;
    summary: string | null;
    content: string;
    author: string | null;
    category: string | null;
    theme: HermesContentTheme | null;
    source_url: string | null;
    metadata: {
      content?: {
        separator?: string;
        tags?: string[];
      };
      reading_time?: number;
    };
    published_at: string | null;
  };
  score: number;
  layer: 'primary' | 'secondary';
}

export interface HermesFeedResponse {
  primary: HermesScoredContent[];
  secondary: HermesScoredContent[];
  meta: {
    total: number;
    threshold: number;
    fallbackUsed: boolean;
    hasMore: boolean;
    offset: number;
    limit: number;
    generatedAt: string;
  };
}

// Process content body by replacing separators with HTML line breaks
function processContentBody(body: string, separator?: string): string {
  if (!separator) return body;
  return body.split(separator).join('<br>');
}

// Converte resposta do Hermes para formato do frontend (exported for testing)
export function transformHermesResponse(response: HermesFeedResponse): GetFeedResponse {
  const allItems = [...response.primary, ...response.secondary];

  const items: FeedItem[] = allItems.map((item) => {
    const separator = item.content.metadata?.content?.separator;
    const processedBody = processContentBody(item.content.content, separator);

    return {
      content: {
        id: item.content.id,
        title: item.content.title,
        titleOriginal: item.content.title,
        summary: item.content.summary || `${processedBody.substring(0, 200)}...`,
        body: processedBody,
        bodyOriginal: item.content.content,
        theme: mapToFrontendTheme(item.content.theme, item.content.category),
        author: item.content.author || 'Unknown',
        source: 'Newsletter',
        sourceUrl: item.content.source_url || '',
        readingTimeMinutes:
          item.content.metadata?.reading_time || calculateReadingTime(processedBody),
        publishedAt: item.content.published_at || new Date().toISOString(),
        createdAt: item.content.published_at || new Date().toISOString(),
      },
      score: item.score,
      tier: item.layer,
      userState: {
        isLiked: false,
        isSaved: false,
        isDismissed: false,
      },
    };
  });

  return {
    items,
    hasMore: response.meta.hasMore,
  };
}

/**
 * Maps backend theme/category to frontend ContentTheme.
 * Supports both new theme enum values and legacy category values.
 */
export function mapToFrontendTheme(theme: string | null, category: string | null): ContentTheme {
  // Backend ContentTheme enum mapping (preferred)
  const themeMapping: Record<string, ContentTheme> = {
    AI: 'ai_ml',
    Engineering: 'engineering',
    Product: 'product',
    Design: 'design',
    Startups: 'startup',
    Business: 'leadership',
    Tools: 'engineering',
  };

  // Legacy category mapping (fallback for old content)
  const categoryMapping: Record<string, ContentTheme> = {
    programming: 'engineering',
    'ai-ml': 'ai_ml',
    devops: 'engineering',
    business: 'leadership',
    science: 'ai_ml',
    fashion: 'design',
    sports: 'career',
  };

  // Prefer theme over category
  if (theme && themeMapping[theme]) {
    return themeMapping[theme];
  }

  if (category && categoryMapping[category]) {
    return categoryMapping[category];
  }

  return 'engineering';
}

/**
 * @deprecated Use mapToFrontendTheme instead
 */
export function mapCategoryToTheme(category: string | null): ContentTheme {
  return mapToFrontendTheme(null, category);
}

// Raw content response from Hermes API (database structure)
interface HermesContentResponse {
  id: string;
  title: string;
  summary: string | null;
  content: string; // This is 'body' in frontend
  author: string | null;
  category: string | null;
  theme: HermesContentTheme | null;
  source_url: string | null;
  metadata: {
    content?: {
      separator?: string;
      tags?: string[];
    };
  };
  published_at: string | null;
  created_at: string;
}

// Transform Hermes content response to frontend Content type
function transformHermesContent(raw: HermesContentResponse): Content {
  const separator = raw.metadata?.content?.separator;
  const processedBody = processContentBody(raw.content, separator);

  return {
    id: raw.id,
    title: raw.title,
    titleOriginal: raw.title,
    summary: raw.summary || `${processedBody.substring(0, 200)}...`,
    body: processedBody,
    bodyOriginal: raw.content,
    theme: mapToFrontendTheme(raw.theme, raw.category),
    author: raw.author || 'Unknown',
    source: 'Newsletter',
    sourceUrl: raw.source_url || '',
    readingTimeMinutes: calculateReadingTime(processedBody),
    publishedAt: raw.published_at || new Date().toISOString(),
    createdAt: raw.created_at || new Date().toISOString(),
  };
}

// Public feed types (no auth required)
export interface PublicFeedItem {
  id: string;
  title: string;
  summary: string | null;
  author: string | null;
  source_url: string | null;
  category: string | null;
  published_at: string | null;
  metadata: unknown;
}

export interface PublicFeedResponse {
  items: PublicFeedItem[];
  total: number;
}

export interface ContentPreviewResponse {
  id: string;
  title: string;
  summary: string | null;
  author: string | null;
  source_url: string | null;
  category: string | null;
  published_at: string | null;
  body_preview: string;
}

// Transform public feed item to FeedItem format
function transformPublicFeedItem(item: PublicFeedItem): FeedItem {
  return {
    content: {
      id: item.id,
      title: item.title,
      titleOriginal: item.title,
      summary: item.summary || '',
      body: undefined, // Not available in public feed
      bodyOriginal: undefined,
      theme: mapCategoryToTheme(item.category),
      author: item.author || 'Unknown',
      source: 'Newsletter',
      sourceUrl: item.source_url || '',
      publishedAt: item.published_at || new Date().toISOString(),
      createdAt: item.published_at || new Date().toISOString(),
    },
    score: 0.5, // Default score for public feed
    tier: 'primary',
    userState: {
      isLiked: false,
      isSaved: false,
      isDismissed: false,
    },
  };
}

// Transform content preview to Content format
function transformContentPreview(preview: ContentPreviewResponse): Content {
  return {
    id: preview.id,
    title: preview.title,
    titleOriginal: preview.title,
    summary: preview.summary || '',
    body: preview.body_preview,
    bodyOriginal: preview.body_preview,
    theme: mapCategoryToTheme(preview.category),
    author: preview.author || 'Unknown',
    source: 'Newsletter',
    sourceUrl: preview.source_url || '',
    publishedAt: preview.published_at || new Date().toISOString(),
    createdAt: preview.published_at || new Date().toISOString(),
  };
}

export const feedService = {
  // Feed (Hermes: GET /feed)
  async getFeed(params?: GetFeedParams): Promise<GetFeedResponse> {
    const response = await apiClient.get<HermesFeedResponse>(
      '/feed',
      params as Record<string, string | number | boolean | undefined>,
    );
    return transformHermesResponse(response);
  },

  // Get content by ID (Hermes: GET /content/:id)
  async getContent(id: string): Promise<FeedItem> {
    return apiClient.get<FeedItem>(`/content/${id}`);
  },

  // Get content summary (Hermes: GET /content/:id/summary)
  async getContentById(id: string): Promise<Content> {
    return apiClient.get<Content>(`/content/${id}/summary`);
  },

  // Get full content (Hermes: GET /content/:id/full)
  async getContentFull(id: string): Promise<Content> {
    const raw = await apiClient.get<HermesContentResponse>(`/content/${id}/full`);
    return transformHermesContent(raw);
  },

  // Public feed (no auth required) - GET /feed/public
  async getPublicFeed(language?: string): Promise<FeedItem[]> {
    const url = new URL(`${config.api.baseUrl}/feed/public`);
    if (language) {
      url.searchParams.append('language', language);
    }

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch public feed');
    }

    const data: PublicFeedResponse = await response.json();
    return data.items.map(transformPublicFeedItem);
  },

  // Content preview (no auth required) - GET /feed/public/content/:id/preview
  async getContentPreview(id: string): Promise<Content> {
    const url = `${config.api.baseUrl}/feed/public/content/${id}/preview`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch content preview');
    }

    const data: ContentPreviewResponse = await response.json();
    return transformContentPreview(data);
  },
};
